import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Overline, Reveal } from "./Reveal";
import { apiFetch, IS_STATIC } from "@/lib/api";
import { getContent, loadRemoteContent, hasLocalOverrides } from "@/lib/staticData";

const FALLBACK = [
  {
    text: "The cut lines are so sharp they look printed. Strovyn treated our 1890s villa like a gallery piece.",
    author: "Margaret H.",
    context: "Heritage restoration — North Adelaide",
  },
  {
    text: "They repainted our entire office across two weekends. Zero disruption on Monday, flawless result.",
    author: "Daniel K.",
    context: "Commercial repaint — Unley",
  },
  {
    text: "Our twenty-year-old kitchen cabinets look factory new. The spray finish is genuinely unbelievable.",
    author: "Priya S.",
    context: "Cabinet refinishing — Norwood",
  },
];

export default function TestimonialsSection() {
  const [quotes, setQuotes] = useState(IS_STATIC ? getContent().testimonials : FALLBACK);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (IS_STATIC) {
      loadRemoteContent().then((c) => {
        if (c && !hasLocalOverrides()) setQuotes(c.testimonials);
      });
      return;
    }
    apiFetch("/testimonials")
      .then((list) => {
        if (Array.isArray(list) && list.length > 0)
          setQuotes(list.map((t) => ({ text: t.text, author: t.author, context: t.context })));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % quotes.length), 6000);
    return () => clearInterval(t);
  }, [quotes.length]);

  const q = quotes[index % quotes.length];

  return (
    <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8 sm:py-32" data-testid="testimonials-section">
      <Reveal>
        <Overline>Word of mouth</Overline>
      </Reveal>
      <div className="mt-8 min-h-[240px] sm:min-h-[220px]">
        <AnimatePresence mode="wait">
          <motion.blockquote
            key={index}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="max-w-4xl font-serif text-2xl uppercase leading-tight tracking-tight sm:text-4xl">
              <span className="text-accent">"</span>
              {q.text}
              <span className="text-accent">"</span>
            </p>
            <footer className="mt-6 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground">
              {q.author}{q.context ? ` — ${q.context}` : ""}
            </footer>
          </motion.blockquote>
        </AnimatePresence>
      </div>
      <div className="mt-8 flex gap-2">
        {quotes.map((_, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            aria-label={`Show testimonial ${i + 1}`}
            data-testid={`testimonial-dot-${i}`}
            className={`h-1 transition-all duration-300 ${
              i === index % quotes.length ? "w-10 bg-accent" : "w-5 bg-border hover:bg-muted-foreground"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
